<template>
  <div>
    <b-row class="ml-2">
      <b-col v-if="isObjectEmpty(entity)" class="position-relative d-flex flex-column">
        <div class="text-muted text-center mt-3 mb-2">
          {{$t('global.no_data')}}
        </div>
      </b-col>

      <b-col v-for="(item, index) in fields" :key="index" xl="12">
        <div v-if="isItemArray(item)" class="mt-2">
          <formFields v-if="item.length > 0"
                      :showSubmitButton="false"
                      :labelBlockWith="3"
                      :inline="true"
                      :fields="item"
                      :prefillParams="prefillParams[index]"
          />
        </div>
        <div v-else class="mb-3">
          <b-card-header
              header-tag="header"
              class="py-3 px-4 pointer border-0"
              v-b-toggle="`entity-${parentIndex}${index}`">
            <div class="d-flex flex-row justify-content-between align-items-center">
              <div>
                <i class="fas fa-sort-down mr-3"></i>
                <span>{{createLabel(prefillParams[index].name)}}</span>
              </div>
            </div>
          </b-card-header>
          <b-collapse :id="`entity-${parentIndex}${index}`">
            <b-card-body class="position-relative d-flex flex-column pt-3 px-4 pb-0">
              <div v-if="isObjectEmpty(prefillParams[index].value)"
                   class="text-muted text-center mt-3 mb-3">
                {{$t('global.no_data')}}
              </div>
              <object-representation v-else
                  :entity="prefillParams[index].value"
                  :read-only="readOnly"
                  :name="prefillParams[index].name"
                  :default-currency="localCurrency"
                  :parent-index="`${parentIndex}${index}`"
                  :is-entity-array="prefillParams[index].isEntityArray"
              />
            </b-card-body>
          </b-collapse>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import formFields from '@/components/global/formFields';
import moment from "moment";
import i18n from "@/utils/i18n";
import {isBoolean, isString, isObject, isArray, isNull, isNil, isEmpty} from 'lodash';

export default {
  name: "objectRepresentation",
  components: {
    formFields
  },
  props: {
    entity: {
      type: Object,
      default: {}
    },
    readOnly: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      default: ''
    },
    defaultCurrency: {
      type: String,
      default: ''
    },
    parentIndex: {
      type: String,
      default: '0'
    },
    isEntityArray: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fields: [],
      prefillParams: [],
      localCurrency: '',
    }
  },
  mounted() {
    this.setFieldsAndParams();
  },
  methods: {
    setFieldsAndParams () {
      if (this.isObjectEmpty(this.entity)) return;
      this.localCurrency = this.entity.currency ? this.entity.currency : this.defaultCurrency;
      for (const key in this.entity) {
        const value = this.entity[key];
        if (!isObject(value)) {
          if (!isArray(this.fields[this.fields.length-1]) || this.fields.length === 0) {
            this.fields.push([]);
            this.prefillParams.push({});
          }
          this.fields[this.fields.length-1].push(this.createField(key, value));
          if (!isNil(value)) {
            const types = ['price', 'amount', 'discount', 'total', 'tax', 'vat', 'rate_sum', 'fee'];
            if (types.some(item => key.includes(item))) {
              this.prefillParams[this.prefillParams.length-1][this.createName(key)] = ((parseInt(value)/100).toFixed(2)).toString() + ` ${this.localCurrency}`;
            } else if (key.includes('percent')) {
              this.prefillParams[this.prefillParams.length-1][this.createName(key)] = value + ' %';
            } else {
              this.prefillParams[this.prefillParams.length-1][this.createName(key)] = value;
            }
          } else {
            this.prefillParams[this.prefillParams.length-1][this.createName(key)] = isNull(value) ? 'null' : 'undefined';
          }
        } else {
          const innerEntity = {name: key, value: value, isEntityArray: isArray(value)};
          if (innerEntity.isEntityArray) {
            if (value.length === 1 && isObject(value[0])) {
              innerEntity.value = value[0];
              innerEntity.isEntityArray = false;
            }
          }
          this.fields.push(null);
          this.prefillParams.push(innerEntity);
        }
      }
    },
    createField (key, value) {
      const fieldObj = {
        name: this.createName(key),
        readOnly: this.readOnly,
        label: this.createLabel(key)
      }
      if (isBoolean(value)) {
        fieldObj.type = 'switch';
      } else {
        fieldObj.type = 'text';
        if (isString(value)) {
          if (moment(value, 'YYYY-MM-DD HH:mm:ss', true).isValid() || moment(value, 'YYYY-MM-DDTHH:mm:ss.SSSZ', true).isValid()) {
            fieldObj.type = 'date';
            fieldObj.withTime = true;
            fieldObj.modelConfig = {timeAdjust: null};
          } else if (moment(value, 'YYYY-MM-DD', true).isValid()) {
            fieldObj.type = 'date';
            fieldObj.withTime = false;
          } else if (key === 'currency' && !this.readOnly) {
            fieldObj.type = 'select';
          }
        }
      }
      if (fieldObj.type === 'date' || fieldObj.type === 'switch') {
        fieldObj.disabled = this.readOnly;
      }
      return fieldObj;
    },
    createLabel (key) {
      if (this.isEntityArray) {
        return (parseInt(key)+1).toString();
      }
      if (i18n.te(`object_representation.field_labels.${key}`)) {
        return i18n.t(`object_representation.field_labels.${key}`);
      }
      return key.match(/[a-zA-Z]+|[0-9]+/g).map((substr) => {
        return substr.toLowerCase() === 'id' || substr.toLowerCase() === 'json' || substr.toLowerCase() === 'vat' ? substr.toUpperCase() : substr.charAt(0).toUpperCase() + substr.slice(1);
      }).join(" ");
    },
    createName (key) {
      return this.readOnly ? `${key}_readOnly` : key;
    },
    isObjectEmpty(obj) {
      return isEmpty(obj);
    },
    isItemArray(item) {
      return isArray(item);
    }
  }
}
</script>
